import React from 'react'
import { GetStaticPaths, GetStaticProps } from 'next'
import { Durations } from '@/constants/durations'
import { paths } from '@/constants/paths'
import { Seo } from '@/layout/Seo'
import { getContentfulClient } from '@/services/ApolloClient'
import { CatalogTitle, CatalogTitleMap, fetchTitles } from '@/services/ContentCatalog'
import { getValidatedTitleMap } from '@/services/RenderService/fetchPageDataContext'
import {
  GetTheatricalLandingPageOldQuery,
  GetTheatricalLandingPageNewQuery,
  UltrawideBanner,
  Faq,
  GetTicketsPageFaqQuery,
} from '@/types/codegen-contentful'
import { getLocaleFromParams } from '@/utils/LocaleUtil'
import DatadogRumFull from '@/utils/datadog/DatadogRumFull'
import { getStaticPropsErrorHandler, getUrlFromThisFilePath } from '@/utils/nextUtils/nextErrorHandlers'
import { getBasePath } from '@/utils/sitemap/base'
import { useTranslate } from '@/utils/translate/translate-client'
import { loadTranslations } from '@/utils/translate/translate-server'
import { isDefined } from '@/utils/types'
import {
  GET_THEATRICAL_LANDING_PAGE_OLD,
  GET_THEATRICAL_LANDING_PAGE_NEW,
  TicketsLandingViewOld,
} from '@/views/TicketsLandingView'
import { GET_TICKETS_PAGE_FAQS, TheatricalLandingReleaseNew } from '@/views/TicketsLandingView/queries'
import { byReleaseDateAsc, byReleaseDateDesc, mapToTicketsLandingRelease } from '@/views/TicketsLandingView/transforms'
import { TicketsLandingRelease } from '@/views/TicketsLandingView/types'

interface TicketsLandingPageProps {
  oldPage: OldPageProps
  newPage: NewPageProps
  locale: string
}

export function TicketsLandingPage(props: TicketsLandingPageProps) {
  const { t } = useTranslate('tickets')
  const basePath = getBasePath(props.locale)
  const url = `${basePath}${paths.tickets.index}`
  const description = t(
    'ticketsLandingPageDescription',
    "Buy tickets and get showtimes for Angel Originals at a theater near you today. See what's currently in theaters or explore upcoming and past releases. Angel Guild members get 2 tickets for every theatrical release as part of their membership.",
  )
  const title = t('ticketLandingPageTitle', 'Tickets & Showtimes | In Theaters | Angel Studios')

  const openGraph = {
    url,
    description,
    title,
  }

  return (
    <>
      <DatadogRumFull />
      <Seo canonical={url} description={description} openGraph={openGraph} path={paths.tickets.index} title={title} />
      <TicketsLandingViewOld {...props.oldPage} />
    </>
  )
}

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  }
}

export const getStaticProps: GetStaticProps = async ({ params, preview = false }) =>
  getStaticPropsErrorHandler({ requestUrl: getUrlFromThisFilePath(params) }, async () => {
    const locale = getLocaleFromParams(params)
    const newPage = await fetchNewPageProps(locale, preview)

    return {
      props: {
        locale,
        oldPage: await fetchOldPageProps(preview),
        newPage,
        ...(await loadTranslations(locale, ['common', 'tickets'])),
      },
      revalidate: newPage.titles.length >= 1 ? Durations.FIFTEEN_MINUTES_IN_SECONDS : Durations.ONE_MINUTE_IN_SECONDS,
    }
  })

interface OldPageProps {
  upcoming: TicketsLandingRelease[]
  inTheaters: TicketsLandingRelease[]
  pastReleases: TicketsLandingRelease[]
  banner?: UltrawideBanner
}
async function fetchOldPageProps(preview: boolean): Promise<OldPageProps> {
  const { data } = await getContentfulClient({
    preview,
  }).query<GetTheatricalLandingPageOldQuery>({
    query: GET_THEATRICAL_LANDING_PAGE_OLD,
    variables: {
      preview,
    },
  })

  const upcoming = data?.upcoming?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateAsc) || []
  const inTheaters =
    data?.inTheaters?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateAsc) || []
  const pastReleases =
    data?.pastReleases?.items?.map(mapToTicketsLandingRelease).filter(isDefined).sort(byReleaseDateDesc) || []
  const banner = (data?.banners?.items?.[0] as UltrawideBanner) ?? undefined

  return {
    upcoming,
    inTheaters,
    pastReleases,
    banner,
  }
}

interface NewPageProps {
  releases: TheatricalLandingReleaseNew[]
  titles: CatalogTitle[]
  titlesMap: CatalogTitleMap
  faqs: Faq[]
}

async function fetchNewPageProps(locale: string, preview: boolean): Promise<NewPageProps> {
  const { data } = await getContentfulClient({
    preview,
  }).query<GetTheatricalLandingPageNewQuery>({
    query: GET_THEATRICAL_LANDING_PAGE_NEW,
    variables: {
      preview,
    },
  })

  const allContentIds = mapAllContentIds(data.theatricalLandingReleaseCollection?.items ?? [])
  const titles = await fetchTitles(locale, allContentIds)

  return {
    releases: [...(data.theatricalLandingReleaseCollection?.items ?? [])],
    titles: titles,
    titlesMap: await getValidatedTitleMap(titles),
    faqs: await fetchFaqs(locale, preview),
  }
}

function mapAllContentIds(theatricalLandingReleases: TheatricalLandingReleaseNew[]) {
  return (
    theatricalLandingReleases
      .filter(isDefined)
      .map((release) => release.contentCatalogId)
      .flat()
      .filter(isDefined) ?? []
  )
}

async function fetchFaqs(locale: string, preview: boolean): Promise<Faq[]> {
  const { data } = await getContentfulClient({
    locale,
    preview,
  }).query<GetTicketsPageFaqQuery>({
    query: GET_TICKETS_PAGE_FAQS,
    variables: {
      locale,
      preview,
    },
  })
  return (data.faqCollection?.items?.filter(isDefined) ?? []) as Faq[]
}

export default TicketsLandingPage
